import { Component, type ErrorInfo, type ReactNode } from 'react';

import { TelemetryContext } from '@tankef/telemetry';
import type { TelemetryProvider } from '@tankef/telemetry';

// ─── Clase base (sin hooks) ───────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  captureError: TelemetryProvider['captureError'];
}

interface State {
  hasError: boolean;
  eventId: string | null;
}

class ErrorBoundaryClass extends Component<Props, State> {
  state: State = { hasError: false, eventId: null };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.captureError(error, {
      componentStack: info.componentStack ?? undefined,
      source: 'ErrorBoundary',
    });

    // Enviar también a GTM para que aparezca en el dataLayer
    window.dataLayer?.push({
      event: 'error_boundary_triggered',
      errorMessage: error.message,
      errorName: error.name,
    });
  }

  handleReload = () => window.location.reload();

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-tkf-bg p-8 text-center">
        <p className="text-4xl">⚠️</p>
        <div className="max-w-md">
          <h1 className="mb-2 text-xl font-bold text-tkf-fg">Algo salió mal</h1>
          <p className="text-sm text-tkf-fg-muted">
            Ocurrió un error inesperado. El equipo ya fue notificado.
          </p>
        </div>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-tkf-fg-on-primary hover:bg-primary-600 transition-colors"
        >
          Recargar página
        </button>
      </div>
    );
  }
}

// ─── Wrapper funcional (puede usar context) ───────────────────────────────────

interface GlobalErrorBoundaryProps {
  children: ReactNode;
}

export function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps) {
  return (
    <TelemetryContext.Consumer>
      {telemetry => (
        <ErrorBoundaryClass
          captureError={telemetry?.captureError ?? ((e: Error) => console.error(e))}
        >
          {children}
        </ErrorBoundaryClass>
      )}
    </TelemetryContext.Consumer>
  );
}
